import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { pathgen } from 'object-path-generator';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { ConfirmationModal, EnhancedAutocomplete, MenuPromotionalAction, Spinner, useOverlayContext } from 'seagull';
import { AgreementTriageModal } from '~/components/AgreementTriageModal/AgreementTriageModal';
import { CardButtonGrid } from '~/components/CardButtonGrid/CardButtonGrid';
import { ImportAgreementModal } from '~/components/ImportAgreementModal/ImportAgreementModal';
import { routes } from '~/constants';
import { useRedirectToProposal } from '~/hooks/useCreateAgreement';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { useRootDevice } from '~/providers/DeviceProvider';
import { genericConnector } from '~/utils/react-hook-form.utils';
import { useProposalTemplates } from '../../hooks/reactQuery/queries/useProposalTemplates';
import { useCreateDraft } from './hooks/useCreateDraft';
import s from './SelectProposalTypeModal.module.scss';
export const SelectProposalTypeModalTestIds = pathgen('SelectProposalTypeModalTestIds');
export const SelectProposalTypeModal = (param)=>{
    let { dismissModal, proposalType } = param;
    const formId = 'create-proposal-from-template';
    const [selectedProposalType, setSelectedProposalType] = useState(proposalType);
    const { openCreationModal } = useRedirectToProposal();
    const { t: selectTexts } = useLocaleKeys('components.selectProposalTypeModal');
    const { templates, isLoading } = useProposalTemplates({
        refreshOnMount: true
    });
    const { createDraft, isInProgress } = useCreateDraft({
        onSuccess: dismissModal
    });
    const { isMobileDevice } = useRootDevice();
    const { pushBottomSheetPage, hideBottomSheet, showModal } = useOverlayContext();
    const { AGREEMENT_FROM_PDF } = useFeatureFlags();
    const { t } = useLocaleKeys();
    const history = useHistory();
    const { control, handleSubmit } = useForm({
        mode: 'all',
        defaultValues: {
            proposalTemplateId: ''
        }
    });
    const showImportOnMobile = isMobileDevice && AGREEMENT_FROM_PDF;
    const proposalTypes = useMemo(()=>[
            {
                title: selectTexts.useTemplate(),
                content: selectTexts.useTemplateContent(),
                onClick: ()=>{
                    setSelectedProposalType('template');
                },
                testId: SelectProposalTypeModalTestIds.ProposalType.Template()
            },
            {
                title: selectTexts.startFromScratch(),
                content: selectTexts.startFromScratchContent(),
                onClick: ()=>{
                    setSelectedProposalType('draft');
                    openCreationModal();
                    dismissModal();
                },
                testId: SelectProposalTypeModalTestIds.ProposalType.Draft()
            }
        ], []);
    let primaryButton = '';
    let cancelButton = '';
    let title = selectTexts.createProposal();
    // switch (selectedProposalType) {
    if (selectedProposalType === 'template') {
        primaryButton = 'Create proposal';
        cancelButton = 'Cancel';
        title = selectTexts.chooseTemplate();
    }
    const createProposalFromTemplate = async (param)=>{
        let { proposalTemplateId } = param;
        if (proposalTemplateId) {
            createDraft(proposalTemplateId);
        }
    };
    const renderTypeSelection = ()=>/*#__PURE__*/ _jsx(CardButtonGrid, {
            testId: SelectProposalTypeModalTestIds.ProposalType.Root(),
            items: proposalTypes.map((type)=>({
                    title: type.title,
                    description: type.content,
                    testId: type.testId,
                    onClick: type.onClick
                }))
        });
    const renderTemplateSelection = ()=>/*#__PURE__*/ _jsx(Controller, {
            control: control,
            name: "proposalTemplateId",
            rules: {
                required: 'Please select a template'
            },
            render: (renderProps)=>{
                const { showErrors, errorMsg } = genericConnector(renderProps);
                return /*#__PURE__*/ _jsx(EnhancedAutocomplete, {
                    options: templates !== null && templates !== void 0 ? templates : [],
                    isLoading: isLoading,
                    className: s.serviceTemplateSelector,
                    keyField: "id",
                    labelField: "title",
                    textFieldProps: {
                        label: selectTexts.templateName(),
                        placeholder: selectTexts.chooseTemplateSubtitle(),
                        error: showErrors,
                        helperText: errorMsg
                    },
                    applyOptionChange: (option)=>{
                        const { field } = renderProps;
                        field.onChange(option === null || option === void 0 ? void 0 : option.id);
                    },
                    newItemOption: {
                        text: selectTexts.createNewTemplate(),
                        onClick: ()=>{
                            history.push(routes.proposalTemplateStudio);
                            return Promise.resolve();
                        }
                    },
                    emptySearchText: selectTexts.emptySearchText(),
                    testId: SelectProposalTypeModalTestIds.ServiceTemplateSelector()
                });
            }
        });
    const showImportAction = showImportOnMobile && selectedProposalType == null;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(ConfirmationModal, {
                showFooter: selectedProposalType === 'template',
                className: s.confirmationModal,
                contentClassName: s.confirmationModalContent,
                style: {
                    padding: '0'
                },
                width: "500px",
                title: title,
                testId: SelectProposalTypeModalTestIds.Root(),
                primaryButtonTestId: SelectProposalTypeModalTestIds.Buttons.CreateProposal(),
                textAlignCenter: true,
                onPrimaryButtonClick: handleSubmit(createProposalFromTemplate),
                onCloseButtonClick: dismissModal,
                cancelButtonDisabled: false,
                primaryButtonLabel: isInProgress ? /*#__PURE__*/ _jsx(Spinner, {
                    size: 40
                }) : primaryButton,
                primaryButtonDisabled: isInProgress,
                primaryButtonFormId: formId,
                secondaryButtonLabel: cancelButton,
                secondaryButtonTestId: SelectProposalTypeModalTestIds.Buttons.Cancel(),
                onSecondaryButtonClick: dismissModal,
                content: /*#__PURE__*/ _jsx("form", {
                    id: formId,
                    onSubmit: handleSubmit(createProposalFromTemplate),
                    children: selectedProposalType === 'template' ? renderTemplateSelection() : renderTypeSelection()
                })
            }),
            showImportAction && /*#__PURE__*/ _jsx(MenuPromotionalAction, {
                title: t.agreementImport.menuItem.title(),
                body: t.agreementImport.menuItem.body(),
                testId: SelectProposalTypeModalTestIds.ProposalType.ImportAgreement(),
                bi: {
                    name: 'import_agreement'
                },
                onClick: ()=>{
                    pushBottomSheetPage({
                        content: /*#__PURE__*/ _jsx(ImportAgreementModal, {
                            dismissModal: hideBottomSheet,
                            onSuccess: (extraction, conversationId)=>{
                                hideBottomSheet();
                                showModal({
                                    closeOnClickOutside: false,
                                    closeOnEscapeKey: true,
                                    biName: 'agreement_triage',
                                    content: (param)=>{
                                        let { dismissModal: dismissTriage } = param;
                                        return /*#__PURE__*/ _jsx(AgreementTriageModal, {
                                            extraction: extraction,
                                            conversationId: conversationId,
                                            dismissModal: dismissTriage
                                        });
                                    }
                                });
                            }
                        })
                    });
                }
            })
        ]
    });
};
